import React from 'react';
import { FiArrowLeft, FiX, FiCheck, FiChevronRight } from 'react-icons/fi';
import { LuMapPin } from 'react-icons/lu';

const LocationModal = ({
  form,
  states,
  lgas,
  lcdas,
  showLcdaField,
  loadingStates,
  loadingLgas,
  loadingLcdas,
  onStateSelect,
  onLgaSelect,
  onLcdaSelect,
  onClose,
  isMobile = false
}) => {
  const getCurrentStep = () => {
    if (form.lga_id && showLcdaField && !form.lcda_id) return 'lcda';
    if (form.state_id && !form.lga_id) return 'lga';
    return 'state';
  };

  // Brand colors
  const colors = {
    primary: '#ff660e',
    primaryLight: '#fff5f0',
    text: '#0f0e0e',
    textLight: '#666666',
    border: '#e5e5e5',
    hoverBg: '#f8f9fa'
  };

  const containerClass = isMobile 
    ? "bg-white rounded-2xl w-full max-w-sm shadow-xl"
    : "bg-white rounded-2xl w-full max-w-md shadow-2xl";
    
  const headerClass = isMobile 
    ? "flex items-center justify-between p-4 border-b"
    : "flex items-center justify-between p-6 border-b";
    
  const contentClass = isMobile 
    ? "p-4 max-h-96 overflow-y-auto"
    : "p-6 max-h-[60vh] overflow-y-auto";
    
  const buttonClass = `w-full flex items-center justify-between ${
    isMobile ? 'p-3' : 'p-4'
  } rounded-lg transition border border-gray-200 hover:border-orange-200 hover:bg-orange-50`;

  const selectedButtonClass = `w-full flex items-center justify-between ${
    isMobile ? 'p-3' : 'p-4'
  } rounded-lg border bg-orange-50 border-orange-300`;

  const currentStep = getCurrentStep();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={containerClass}>
        {/* Modal Header */}
        <div className={headerClass} style={{ borderColor: colors.border }}>
          {form.state_id && currentStep !== 'state' ? (
            <button
              onClick={() => {
                if (currentStep === 'lga') onStateSelect('');
                if (currentStep === 'lcda') onLgaSelect('');
              }}
              disabled={loadingLgas || loadingLcdas}
              className="p-2 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
              style={{ color: colors.text }}
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <div className="w-10"></div>
          )}
          
          <h3 
            className={`${isMobile ? "text-base" : "text-lg"} font-semibold`}
            style={{ color: colors.text }}
          >
            {currentStep === 'lcda' ? 'Select LCDA' :
             currentStep === 'lga' ? 'Select LGA' : 'Select State'}
          </h3>
          
          <button
            onClick={onClose}
            disabled={loadingLgas || loadingLcdas}
            className="p-2 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
            style={{ color: colors.text }}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className={contentClass}>
          {currentStep === 'state' ? (
            <div>
              <h4 
                className={`${isMobile ? "text-sm" : "text-base"} font-medium mb-4`}
                style={{ color: colors.text }}
              >
                States
              </h4>
              
              {loadingStates ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div 
                    className="animate-spin rounded-full h-8 w-8 border-b-2"
                    style={{ borderColor: colors.primary }}
                  ></div>
                  <p className="mt-3" style={{ color: colors.textLight }}>
                    Loading states...
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {states.map((state) => (
                    <button
                      key={state.value}
                      onClick={() => onStateSelect(state.value)}
                      disabled={loadingLgas}
                      className={buttonClass}
                    >
                      <div className="flex items-center gap-3">
                        <LuMapPin className="w-4 h-4" style={{ color: colors.primary }} />
                        <span 
                          className="font-medium"
                          style={{ color: colors.text }}
                        >
                          {state.label}
                        </span>
                      </div>
                      <FiChevronRight className="w-5 h-5" style={{ color: colors.textLight }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : currentStep === 'lga' ? (
            <div>
              <h4 
                className={`${isMobile ? "text-sm" : "text-base"} font-medium mb-4`}
                style={{ color: colors.text }}
              >
                Local Government Areas (LGA)
              </h4>
              
              {loadingLgas ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div 
                    className="animate-spin rounded-full h-8 w-8 border-b-2"
                    style={{ borderColor: colors.primary }}
                  ></div>
                  <p className="mt-3" style={{ color: colors.textLight }}>
                    Loading LGAs...
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {lgas.map((lga) => (
                    <button
                      key={lga.value}
                      onClick={() => onLgaSelect(lga.value)}
                      disabled={loadingLcdas}
                      className={buttonClass}
                    >
                      <div className="flex items-center gap-3">
                        <LuMapPin className="w-4 h-4" style={{ color: colors.primary }} />
                        <span 
                          className="font-medium"
                          style={{ color: colors.text }}
                        >
                          {lga.label}
                        </span>
                      </div>
                      <FiChevronRight className="w-5 h-5" style={{ color: colors.textLight }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <h4 
                className={`${isMobile ? "text-sm" : "text-base"} font-medium mb-4`}
                style={{ color: colors.text }}
              >
                Local Council Development Areas (LCDA)
              </h4>
              
              {loadingLcdas ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div 
                    className="animate-spin rounded-full h-8 w-8 border-b-2"
                    style={{ borderColor: colors.primary }}
                  ></div>
                  <p className="mt-3" style={{ color: colors.textLight }}>
                    Loading LCDAs...
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {lcdas.map((lcda) => (
                    <button
                      key={lcda.value}
                      onClick={() => onLcdaSelect(lcda.value)}
                      className={form.lcda_id === lcda.value ? selectedButtonClass : buttonClass}
                    >
                      <div className="flex items-center gap-3">
                        <LuMapPin 
                          className="w-4 h-4" 
                          style={{ 
                            color: form.lcda_id === lcda.value 
                              ? colors.primary 
                              : colors.primary 
                          }} 
                        />
                        <span 
                          className="font-medium"
                          style={{ color: colors.text }}
                        >
                          {lcda.label}
                        </span>
                      </div>
                      {form.lcda_id === lcda.value && (
                        <FiCheck className="w-5 h-5" style={{ color: colors.primary }} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationModal;